/**
 * useSoftphone — Twilio Voice SDK integration for browser-based softphone.
 * 
 * Registers a Twilio Device so inbound calls via <Client>userId</Client>
 * ring in the agent's browser. Also supports outbound calls.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Device, type Call } from '@twilio/voice-sdk';
import { phoneAPI } from '../lib/api/phone';

export type SoftphoneStatus = 'disconnected' | 'connecting' | 'ready' | 'ringing' | 'on-call' | 'error';

interface SoftphoneState {
  status: SoftphoneStatus;
  error: string | null;
  activeCall: Call | null;
  incomingCall: Call | null;
  callDuration: number;
  callerInfo: { from: string; to: string } | null;
}

export function useSoftphone() {
  const deviceRef = useRef<Device | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [state, setState] = useState<SoftphoneState>({
    status: 'disconnected',
    error: null,
    activeCall: null,
    incomingCall: null,
    callDuration: 0,
    callerInfo: null,
  });

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      setState(s => ({ ...s, callDuration: Math.floor((Date.now() - start) / 1000) }));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setState(s => ({ ...s, callDuration: 0 }));
  }, []);

  // Initialize Twilio Device
  const connect = useCallback(async () => {
    try {
      setState(s => ({ ...s, status: 'connecting', error: null }));
      const res = await phoneAPI.voiceToken();
      const token = (res as any)?.token;
      if (!token) {
        setState(s => ({ ...s, status: 'error', error: 'Failed to get voice token' }));
        return;
      }

      const device = new Device(token, {
        edge: 'ashburn',
        closeProtection: true,
      });

      device.on('registered', () => {
        setState(s => ({ ...s, status: 'ready', error: null }));
        // Set agent status to online in V3
        phoneAPI.agentStatus.update('online').catch(() => {});
      });

      device.on('error', (err) => {
        console.error('[Softphone] Device error:', err.message);
        setState(s => ({ ...s, status: 'error', error: err.message }));
      });

      device.on('incoming', (call: Call) => {
        console.log('[Softphone] Incoming call from:', call.parameters.From);
        setState(s => ({
          ...s,
          status: 'ringing',
          incomingCall: call,
          callerInfo: { from: call.parameters.From || 'Unknown', to: call.parameters.To || '' },
        }));

        call.on('accept', () => {
          setState(s => ({ ...s, status: 'on-call', activeCall: call, incomingCall: null }));
          startTimer();
        });

        call.on('disconnect', () => {
          setState(s => ({ ...s, status: 'ready', activeCall: null, incomingCall: null, callerInfo: null }));
          stopTimer();
        });

        call.on('cancel', () => {
          setState(s => ({ ...s, status: 'ready', incomingCall: null, callerInfo: null }));
        });
      });

      device.on('tokenWillExpire', async () => {
        try {
          const refreshRes = await phoneAPI.voiceToken();
          const refreshToken = (refreshRes as any)?.token;
          if (refreshToken) {
            device.updateToken(refreshToken);
          }
        } catch { /* token refresh failed */ }
      });

      await device.register();
      deviceRef.current = device;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[Softphone] Init error:', msg);
      setState(s => ({ ...s, status: 'error', error: msg }));
    }
  }, [startTimer, stopTimer]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
    }
    stopTimer();
    phoneAPI.agentStatus.update('offline').catch(() => {});
    setState({ status: 'disconnected', error: null, activeCall: null, incomingCall: null, callDuration: 0, callerInfo: null });
  }, [stopTimer]);

  // Accept incoming call
  const accept = useCallback(() => {
    if (state.incomingCall) {
      state.incomingCall.accept();
    }
  }, [state.incomingCall]);

  // Reject incoming call
  const reject = useCallback(() => {
    if (state.incomingCall) {
      state.incomingCall.reject();
      setState(s => ({ ...s, status: 'ready', incomingCall: null, callerInfo: null }));
    }
  }, [state.incomingCall]);

  // Hang up active call
  const hangup = useCallback(() => {
    if (state.activeCall) {
      state.activeCall.disconnect();
    }
    if (state.incomingCall) {
      state.incomingCall.reject();
    }
    stopTimer();
    setState(s => ({ ...s, status: 'ready', activeCall: null, incomingCall: null, callerInfo: null }));
  }, [state.activeCall, state.incomingCall, stopTimer]);

  // Make outbound call
  const makeCall = useCallback(async (to: string, options?: { customerId?: string; jobId?: string }) => {
    if (!deviceRef.current) return;
    try {
      // Create call record in DB first so it appears in call history immediately
      await phoneAPI.calls.create({ to, customerId: options?.customerId, jobId: options?.jobId });

      const call = await deviceRef.current.connect({ params: { To: to } });
      setState(s => ({ ...s, status: 'on-call', activeCall: call, callerInfo: { from: 'You', to } }));
      startTimer();

      call.on('disconnect', () => {
        setState(s => ({ ...s, status: 'ready', activeCall: null, callerInfo: null }));
        stopTimer();
      });
    } catch (err) {
      console.error('[Softphone] Call error:', err);
    }
  }, [startTimer, stopTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (deviceRef.current) deviceRef.current.destroy();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    accept,
    reject,
    hangup,
    makeCall,
    isConnected: state.status !== 'disconnected' && state.status !== 'error',
  };
}
